
import React, { useState } from 'react';
import { useLocation, Link, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
      '@/components/ui/dropdown-menu';
import { createPageUrl } from '@/utils';
import {
  Home,
  Briefcase,
  Calendar,
  CheckSquare,
  BarChart2,
  Users,
  Settings,
  Bell,
  Search,
  ChevronLeft,
  UploadCloud,
  User, // Added User icon for Profile
  Building // Added Building icon for Subsidiary Management
} from
      'lucide-react';

export default function AppLayout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('capexia.sidebar') === 'collapsed'
    } catch { return false }
  })

  const isActive = (name: string) => {
    try {
      const target = createPageUrl(name as any).split('?')[0]
      // Root path must match exactly, otherwise it is always active
      if (target === '/') return location.pathname === '/'
      // For nested routes, startsWith is fine (e.g., /projects and /projects/123)
      return location.pathname.startsWith(target)
    } catch {
      return location.pathname.toLowerCase().includes(String(name).toLowerCase())
    }
  };

  return (
      <div className="min-h-screen w-full bg-gray-50">
        {/* Sidebar (fixed) */}
        <aside className={`fixed inset-y-0 left-0 z-40 ${collapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 flex flex-col` }>
          {/* Header */}
          <div className={`py-3 border-b flex items-center justify-center ${collapsed ? 'px-0' : 'px-6'}`}>
            <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68a9ceb78a3b2f5c335c3a70/3ab542b70_Capexia_logo.png" alt="CAPEXIA Logo" className={`${collapsed ? 'h-8' : 'h-10'} object-contain`} />
          </div>

          {/* Navigation */}
          <nav className="p-4">
            <div className="space-y-2">
              <Link to={createPageUrl("Dashboard")}>
                <Button
                    variant={isActive('Dashboard') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Dashboard') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Home className="w-4 h-4" />
                  {!collapsed && <span>Tableau de bord</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("Projects")}>
                <Button
                    variant={isActive('Projects') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Projects') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Briefcase className="w-4 h-4" />
                  {!collapsed && <span>Projets CAPEX</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("Planning")}>
                <Button
                    variant={isActive('Planning') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Planning') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Calendar className="w-4 h-4" />
                  {!collapsed && <span>Planification</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("Validation")}>
                <Button
                    variant={isActive('Validation') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Validation') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <CheckSquare className="w-4 h-4" />
                  {!collapsed && <span>Validation</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("Reports")}>
                <Button
                    variant={isActive('Reports') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Reports') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <BarChart2 className="w-4 h-4" />
                  {!collapsed && <span>Rapports</span>}
                </Button>
              </Link>
            </div>
          </nav>

          {/* Administration (bottom) */}
          <div className="mt-auto p-4">
            {!collapsed && <h3 className="px-1 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Administration</h3>}
            <div className="space-y-2">
              <Link to={createPageUrl("SubsidiaryManagement")}>
                <Button
                    variant={isActive('SubsidiaryManagement') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('SubsidiaryManagement') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Building className="w-4 h-4" />
                  {!collapsed && <span>Gestion des filiales</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("UserManagement")}>
                <Button
                    variant={isActive('UserManagement') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('UserManagement') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Users className="w-4 h-4" />
                  {!collapsed && <span>Utilisateurs</span>}
                </Button>
              </Link>

              <Link to={createPageUrl("Settings")}>
                <Button
                    variant={isActive('Settings') ? "default" : "ghost"}
                    className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2 ${isActive('Settings') ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}>
                  <Settings className="w-4 h-4" />
                  {!collapsed && <span>Configuration</span>}
                </Button>
              </Link>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t">
            <Button
              variant="ghost"
              className={`w-full ${collapsed ? 'justify-center' : 'justify-start'} gap-2`}
              onClick={() => {
                const next = !collapsed
                setCollapsed(next)
                try { localStorage.setItem('capexia.sidebar', next ? 'collapsed' : 'expanded') } catch {}
              }}
            >
              {collapsed ? <ChevronLeft className="w-4 h-4 rotate-180" /> : <ChevronLeft className="w-4 h-4" />}
              {!collapsed && 'Réduire le menu'}
            </Button>
          </div>
        </aside>

        {/* Main Content (offset for fixed sidebar) */}
        <main className={`flex flex-col ${collapsed ? 'ml-16' : 'ml-64'}`}>
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                      placeholder="Rechercher un projet, budget..."
                      className="w-80 pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button variant="outline" className="gap-2">
                  <UploadCloud className="w-4 h-4" />
                  Import Budget
                </Button>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="w-5 h-5" />
                  <Badge className="absolute -top-1 -right-1 w-2 h-2 p-0 bg-red-500" />
                </Button>

                {/* Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Avatar className="w-8 h-8 cursor-pointer">
                      <AvatarFallback className="bg-teal-600 text-white">SM</AvatarFallback>
                    </Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl("Profile")} className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Mon Profil
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl("Settings")} className="flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Paramètres
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2 text-red-600 cursor-pointer">
                      <ChevronLeft className="w-4 h-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="flex-1 overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>);

}
